<template>
    <v-text-field
        v-if="displayFieldFocused"
        :value="displayValue"
        :placeholder="placeholder"
        dense
        readonly
        :disabled="disabled"
        outlined
        :label="label || labelTextField"
        :hide-details="displayHideDetails"
        :clearable="clearable"
        :rules="rules"
        :tabindex="tabindex"
        @focus="displayFieldFocused = false"
        @click.stop="displayFieldFocused = false"
        @click:append="displayFieldFocused = false"
        @click:clear="onClearClick"
    />
    <v-autocomplete
        v-else
        v-model="internalSelection"
        dense
        outlined
        :clearable="clearable"
        :label="label || labelAutoComplete"
        :loading="loading"
        :items="collection?.models || []"
        :item-text="itemText"
        :item-value="itemValue"
        :search-input.sync="search"
        return-object
        :hide-details="displayHideDetails"
        :rules="rules"
        :disabled="disabled"
        hide-no-data
        autofocus
        no-filter
        @blur="displayFieldFocused = true"
        @focus="onAutocompleteFocus"
    >
        <template #item="{ parent, item, on, attrs }">
            <slot name="item" :parent="parent" :item="item" :on="on" :attrs="attrs">
                <div>{{ item[itemText] }}</div>
            </slot>
        </template>
    </v-autocomplete>
</template>

<script>
import BaseCollection from '@/models/BaseCollection';
import { errToStr, delayed } from '@/helpers/tools';
import { mapActions } from 'vuex';

export default {
    props: {
        selected: {
            type: Object,
            default: null,
        },
        label: {
            type: String,
            default: null,
        },
        labelAutoComplete: {
            type: String,
            default: '',
        },
        labelTextField: {
            type: String,
            default: '',
        },
        displayValue: {
            type: String,
            default: null,
        },
        itemText: {
            type: String,
            default: 'title',
        },
        itemValue: {
            type: String,
            default: 'id',
        },
        queryParam: {
            type: String,
            default: 'query',
        },
        displayHideDetails: {
            type: [Boolean, String],
            default: null,
        },
        collection: {
            type: BaseCollection,
            required: true,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: null,
        },
        rules: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        sorter: {
            type: [Object, Array],
            default: () => null,
        },
        tabindex: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            internalSelection: null,
            loading: false,
            search: null,
            displayFieldFocused: true,
        };
    },

    watch: {
        search(val) {
            if (this.internalSelection && this.internalSelection[this.itemText] === val) {
                return;
            }
            val && val.length >= 3 && this.queryData(val);
        },
        selected(val) {
            this.internalSelection = val;
        },
        internalSelection(val) {
            this.$emit('selected', val);
            // We set the search value to the same value as the display
            // value: It _seems_ that the Autocomplete field marks the text in the
            // textfield if the 'search-input' value is the same as the value in
            // item-text.... WTF??
            // Anyway, we WANT the text to be selected (so that the user can directly begin typing),
            // and this is the "workaround" that makes it possible....
            if (val) {
                this.search = val[this.itemText];
            }
        },
    },

    methods: {
        ...mapActions(['runAsync']),
        queryData: delayed(async function (val) {
            this.loading = true;
            // this.items = [];
            try {
                if (this.sorter) {
                    this.collection.setRemoteSort(this.sorter);
                }
                await this.collection.query({ [this.queryParam]: val });
                // this.items = [...this.collection.models];
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.loading = false;
            }
        }, 250),

        onClearClick() {
            if (this.internalSelection === null) {
                this.$emit('selected', null);
            } else {
                this.internalSelection = null;
            }
        },

        onAutocompleteFocus() {
            // we need to attach the tabindex attribute to the dom input element, as the Vuetify element does not support it:
            this.$el.querySelector('input').setAttribute('tabindex', this.tabindex);
        },
    },
};
</script>
